import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAccount, useDisconnect, useSignMessage } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { getSupabase, generateNonce, signInWithWallet, signOut } from '../lib/supabase';
import { buildSiweMessage, messageToString, getDomain, getOrigin } from '../lib/siwe';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [signing, setSigning] = useState(false);

  // Wagmi hooks
  const { address, isConnected, chainId, connector } = useAccount();
  const { disconnect: wagmiDisconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { open } = useAppKit();

  // Initialize: Check for existing session
  useEffect(() => {
    const initAuth = async () => {
      try {
        const supabase = getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // User has existing session
          setUser({
            id: session.user.id,
            address: session.user.user_metadata?.wallet_address || address,
          });
          setIsWalletConnected(!!address);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Track if connection was user-initiated
  const userInitiatedRef = useRef(false);

  // Watch wallet connection state
  useEffect(() => {
    setIsWalletConnected(isConnected && !!address);
    
    // Only trigger SIWE if:
    // 1. Wallet is connected
    // 2. User is not already authenticated
    // 3. Connection was user-initiated (not auto-connect)
    // 4. Not currently signing
    // 5. Not loading
    if (isConnected && address && !user && !signing && !loading && userInitiatedRef.current) {
      handleSiweSignIn();
      userInitiatedRef.current = false; // Reset after handling
    }
  }, [isConnected, address, user, signing, loading]);

  // Watch for auth state changes
  useEffect(() => {
    const supabase = getSupabase();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser({
          id: session.user.id,
          address: session.user.user_metadata?.wallet_address || address,
        });
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsWalletConnected(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [address]);

  /**
   * Handle SIWE sign in (called automatically when wallet connects)
   */
  const handleSiweSignIn = useCallback(async () => {
    if (!address || signing) return;

    try {
      setSigning(true);
      setLoading(true);

      // Step 1: Generate nonce
      const { data: nonceData, error: nonceError } = await generateNonce();
      if (nonceError || !nonceData?.nonce) {
        throw new Error('Failed to generate nonce');
      }

      // Step 2: Build SIWE message
      const domain = getDomain();
      const origin = getOrigin();
      const currentChainId = chainId || 1; // Default to mainnet

      const siweMessage = buildSiweMessage(
        address,
        nonceData.nonce,
        domain,
        origin,
        currentChainId
      );

      const messageString = messageToString(siweMessage);

      // Step 3: Ensure connector is ready before signing
      if (!connector) {
        throw new Error('Wallet connector not ready. Please try connecting again.');
      }

      // Wait a bit for connector to fully initialize
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 4: Sign message with wallet
      let signature;
      try {
        signature = await signMessageAsync({ message: messageString });
      } catch (signError) {
        if (signError.message?.includes('User rejected') || signError.message?.includes('rejected')) {
          throw new Error('User rejected the signature request');
        }
        if (signError.message?.includes('getChainId') || signError.message?.includes('connector')) {
          // Retry after a short delay if connector wasn't ready
          console.warn('Connector not ready, retrying...');
          await new Promise(resolve => setTimeout(resolve, 500));
          signature = await signMessageAsync({ message: messageString });
        } else {
          throw signError;
        }
      }

      // Step 5: Verify and create session
      const { data: authData, error: authError } = await signInWithWallet(
        address,
        signature,
        messageString
      );

      if (authError) {
        throw new Error(authError.message || 'Authentication failed');
      }

      // Step 6: Wallet record is already created in signInWithWallet
      // No need to call upsertWallet again

      // Step 7: Update user state
      setUser({
        id: authData.user.id,
        address: address,
      });
      setIsWalletConnected(true);

      // Get Supabase session
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.warn('No Supabase session created. Using wallet-based auth.');
      }

    } catch (error) {
      console.error('Error signing in:', error);
      // Don't throw - let user retry
    } finally {
      setSigning(false);
      setLoading(false);
    }
  }, [address, chainId, signMessageAsync, signing, connector]);

  /**
   * Sign in - opens Reown modal
   */
  const signIn = useCallback(() => {
    userInitiatedRef.current = true; // Mark as user-initiated
    open();
  }, [open]);

  /**
   * Sign out
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);

      // Sign out from Supabase
      await signOut();

      // Disconnect wallet
      wagmiDisconnect();

      // Clear local state
      setUser(null);
      setIsWalletConnected(false);
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setLoading(false);
    }
  }, [wagmiDisconnect]);

  const value = {
    user,
    loading: loading || signing,
    signIn,
    logout,
    isAuthenticated: !!user,
    walletAddress: address || user?.address || null,
    isWalletConnected,
    signing,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
